import { PaymentResultStatus, type PaymentResult } from "@/types";

const DEFAULT_MESSAGE_KEY = undefined;

/**
 * Single source of truth for deciding when to show a payment error and what message to show.
 * Used by both legacy and new payment confirmation UIs so all flows (saved card, new card,
 * Apple Pay, wallet) and all API shapes (top-level error_message/error_code, or errors[0])
 * are handled consistently.
 *
 * - Always shows a payment error to the customer when the result indicates failure.
 * - For unpredicted or missing messages, uses the default (UNKNOWN) message so the user never sees a blank error.
 *
 * @param paymentResult - session.paymentResult from checkout session
 * @param getMessage - callback to get localized message for an error code (e.g. getPaymentErrorMessage); called with undefined for default message
 * @returns [hasError, message] - show error banner when hasError is true; message is never empty when hasError is true
 */
export function parsePaymentResult(
  paymentResult: PaymentResult,
  getMessage: (code: string | undefined) => string,
): [boolean, string] {
  const { errorCode = "", status, errorMessage = "" } = paymentResult;
  const defaultMessage = getMessage(DEFAULT_MESSAGE_KEY);

  // Known failure statuses from API (declined, denied, pending, network_error, failed, etc.): always show error
  if (status !== PaymentResultStatus.UNKNOWN) {
    const message = getMessage(errorCode || undefined);
    return [true, message.trim() || defaultMessage];
  }

  // UNKNOWN covers unmapped API statuses or initial state. Show error only when we have error details.
  if (errorCode || (errorMessage && errorMessage.trim())) {
    const message =
      (errorMessage && errorMessage.trim()) ||
      getMessage(errorCode || undefined);
    return [true, (message && message.trim()) || defaultMessage];
  }

  return [false, ""];
}
